/**
 * Reader-side animation runtime for the Published Reader.
 *
 * This module is purely additive and CSS-based — no external animation
 * libraries. It is the runtime counterpart to the `animationConfig.js`
 * resolver (task 2.1): it reuses that module's `animationStyleFor` descriptor
 * and the canonical class/keyframes ids so the builder preview and the
 * published reader animate identically (Builder–Reader Parity).
 *
 * It exposes:
 *   1. `AnimationKeyframes` — a `<style>` block (mounted once via a stable id)
 *      defining `@keyframes` for every animation type, keyed by the SAME ids
 *      returned by `keyframesIdForType` (e.g. `avps-anim-fade`). It also stages
 *      scroll-triggered regions hidden until the play class is added.
 *   2. `AnimatedRegion` — a wrapper that applies the resolved descriptor:
 *      `trigger:'load'` plays immediately; `trigger:'scroll'` stays hidden and
 *      plays when it scrolls into view (via IntersectionObserver). When
 *      IntersectionObserver is unavailable, it resolves to the played
 *      end-state immediately so content is never stuck hidden (Requirement 6.7).
 *
 * Validates (conceptually): Requirements 6.7, 6.8, 6.9.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ANIMATION_BASE_CLASS,
  ANIMATION_PLAY_CLASS,
  ANIMATION_TYPES,
  animationStyleFor,
  keyframesIdForType
} from './animationConfig.js';

/** Stable id so the keyframes `<style>` mounts only once per document. */
export const ANIMATION_STYLE_ELEMENT_ID = 'avps-anim-keyframes';

/** Modifier class the resolver applies to scroll-triggered regions. */
const SCROLL_CLASS = `${ANIMATION_BASE_CLASS}--scroll`;

/**
 * Keyframes body for each animation type, keyed by the type token. The actual
 * `@keyframes` id is derived from `keyframesIdForType(type)` so it always
 * matches the resolver descriptor's `animationName`.
 */
const KEYFRAME_BODIES = {
  fade: `from { opacity: 0; } to { opacity: 1; }`,
  'slide-in': `from { opacity: 0; transform: translateX(-24px); } to { opacity: 1; transform: translateX(0); }`,
  'slide-up': `from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); }`,
  'slide-down': `from { opacity: 0; transform: translateY(-24px); } to { opacity: 1; transform: translateY(0); }`,
  'slide-left': `from { opacity: 0; transform: translateX(24px); } to { opacity: 1; transform: translateX(0); }`,
  'slide-right': `from { opacity: 0; transform: translateX(-24px); } to { opacity: 1; transform: translateX(0); }`,
  zoom: `from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); }`
};

/**
 * Build the full CSS text: one `@keyframes` rule per type plus the scroll
 * staging rules. Scroll regions are hidden (opacity:0) and paused until the
 * play class is added, at which point the animation runs to its end-state.
 */
const buildKeyframesCss = () => {
  const frames = ANIMATION_TYPES
    .map((type) => {
      const id = keyframesIdForType(type);
      const body = KEYFRAME_BODIES[type];
      if (!id || !body) return null;
      return `@keyframes ${id} { ${body} }`;
    })
    .filter(Boolean)
    .join('\n');

  // Scroll-triggered regions start hidden + paused; adding the play class runs
  // the animation. `!important` on the play state overrides the inline
  // `animation-play-state: paused` the resolver stages on the element.
  const scrollRules = [
    `.${SCROLL_CLASS} { opacity: 0; }`,
    `.${SCROLL_CLASS}.${ANIMATION_PLAY_CLASS} { opacity: 1; animation-play-state: running !important; }`
  ].join('\n');

  return `${frames}\n${scrollRules}`;
};

const KEYFRAMES_CSS = buildKeyframesCss();

/**
 * Renders the shared keyframes `<style>` block. The stable id keeps it from
 * being duplicated if the component is mounted in more than one place.
 */
export const AnimationKeyframes = () => (
  <style id={ANIMATION_STYLE_ELEMENT_ID} data-avps-animations="true">
    {KEYFRAMES_CSS}
  </style>
);

/**
 * Wrapper that applies a resolved Animation_Config to its subtree.
 *
 * @param {object} props
 * @param {object|null} [props.config]    Animation_Config to resolve.
 * @param {React.ElementType} [props.as]  Element/component to render (default 'div').
 * @param {object} [props.style]          Caller style, merged over the descriptor.
 * @param {string} [props.className]      Caller class, merged with the descriptor.
 * @param {React.ReactNode} [props.children]
 */
export const AnimatedRegion = ({
  config,
  as: Tag = 'div',
  style,
  className,
  children,
  ...rest
}) => {
  const ref = useRef(null);
  const descriptor = useMemo(() => animationStyleFor(config), [config]);
  const isScroll = Boolean(descriptor && descriptor.className.includes(SCROLL_CLASS));

  // Load animations (and identity) need no play gating. Scroll starts un-played.
  const [played, setPlayed] = useState(false);

  useEffect(() => {
    if (!isScroll || played) return undefined;
    const el = ref.current;
    if (!el) return undefined;

    // No IntersectionObserver (older/SSR-ish environments): never leave the
    // region hidden — resolve straight to the played end-state.
    if (typeof IntersectionObserver === 'undefined') {
      setPlayed(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setPlayed(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isScroll, played]);

  // Identity: no animation descriptor — render the wrapper unchanged.
  if (!descriptor) {
    return (
      <Tag ref={ref} className={className} style={style} {...rest}>
        {children}
      </Tag>
    );
  }

  const mergedClassName =
    [descriptor.className, isScroll && played ? ANIMATION_PLAY_CLASS : null, className]
      .filter(Boolean)
      .join(' ') || undefined;

  // For a played scroll region, force the running/visible end-state inline too,
  // so the animation starts even if the play-class rule is somehow missing.
  const descriptorStyle =
    isScroll && played
      ? { ...descriptor.style, opacity: undefined, animationPlayState: 'running' }
      : descriptor.style;

  const mergedStyle = { ...descriptorStyle, ...style };

  return (
    <Tag ref={ref} className={mergedClassName} style={mergedStyle} {...rest}>
      {children}
    </Tag>
  );
};

export default AnimatedRegion;
